import React, { useState, useEffect, useRef } from 'react'
import { Calendar } from 'primereact/calendar'
import { Dropdown } from 'primereact/dropdown'
import { InputText } from 'primereact/inputtext'
import { Button } from 'primereact/button'
import { InputTextarea } from 'primereact/inputtextarea'
import Modals from '../../shared/Modals'
import server from '../../../server'
import { ToastPopUp } from '../../shared/utils'
import { Toast } from 'primereact/toast'

export default function TransactionPopup({
  visible,
  setVisible,
  paymentData,
  onSave,
  id,
}) {
  const toast = useRef(null)
  const [loading, setLoading] = useState(false)
  const [transactionData, setTransactionData] = useState({
    paymentStatus: 'Pending',
    paymentDate: null,
    transactionId: '',
    paReason: '',
  })

  console.log(id)
  useEffect(() => {
    // Initialize with provided payment data when modal opens
    if (paymentData && visible) {
      setTransactionData({
        paymentStatus: paymentData.paymentStatus || 'Pending',
        paymentDate: paymentData.paPaymentDate
          ? new Date(paymentData.paPaymentDate)
          : null,
        transactionId: paymentData.paPaymentTransactionId || '',
        paReason: paymentData.paReason || '',
      })
    }
  }, [paymentData, visible])

  // New function to fetch payment data when status changes to Paid
  const fetchPaymentData = async (paymentId) => {
    try {
      setLoading(true)
      const response = await server.get(`/admins/payables/${paymentId}`)

      const fetchedData = response.data.data

      // Update the transaction data with fetched values
      setTransactionData((prev) => ({
        ...prev,
        paymentDate: fetchedData.paPaymentDate
          ? new Date(fetchedData.paPaymentDate)
          : new Date(), // Default to current date if not available
        transactionId:
          fetchedData.paPaymentId || fetchedData.paPaymentTransactionId || '',
        paReason: fetchedData.paReason || '',
      }))

      console.log('Fetched payment data:', fetchedData)
    } catch (error) {
      console.error('Error fetching payment data:', error)
      ToastPopUp(toast, 'error', 'Error', 'Failed to fetch payment details')
    } finally {
      setLoading(false)
    }
  }

  const statusOptions = [
    { name: 'Paid', code: 'Paid' },
    { name: 'Pending', code: 'Pending' },
    { name: 'Cancelled', code: 'Cancelled' },
  ]

  const handleChange = (field, value) => {
    setTransactionData((prev) => ({
      ...prev,
      [field]: value,
    }))

    // If status is changed to Paid, fetch the payment data
    if (field === 'paymentStatus' && value === 'Paid' && id) {
      fetchPaymentData(id)
    }
  }

  const handleSave = () => {
    // Format the data according to the expected API structure
    const formattedData = {
      paymentStatus: transactionData.paymentStatus,
      paPaymentTransactionId: transactionData.transactionId,
      paPaymentDate: transactionData.paymentDate
        ? transactionData.paymentDate.toISOString()
        : null,
    }

    // Only add paReason if the status is not Paid
    if (transactionData.paymentStatus !== 'Paid') {
      formattedData.paReason = transactionData.paReason || null
    }

    setLoading(true)

    // Make the PATCH request directly
    server
      .patch(`/admins/payables/${id}`, formattedData)
      .then((response) => {
        // Handle successful response
        setLoading(false)
        console.log('Server response:', response.data)
        ToastPopUp(
          toast,
          'success',
          'Success',
          'Payment status updated successfully'
        )

        // Close the modal
        setVisible(false)

        // If onSave is provided, call it with the response data
        if (onSave) {
          const updatedData = response.data.data
          onSave(updatedData)
        }
      })
      .catch((error) => {
        // Handle error
        setLoading(false)
        console.error('Update error:', error)
        ToastPopUp(
          toast,
          'error',
          'Error',
          error.response?.data?.message || 'Failed to update payment status'
        )
      })
  }

  const isPaid = transactionData.paymentStatus === 'Paid'
  const isCancelled = transactionData.paymentStatus === 'Cancelled'
  const isPending = transactionData.paymentStatus === 'Pending'

  const footerContent = (
    <div className='d-flex justify-content-end gap-2'>
      <Button
        label='Close'
        icon='pi pi-times'
        outlined
        onClick={() => setVisible(false)}
      />
      <Button
        label={
          isPaid
            ? 'Update'
            : isCancelled
            ? 'Confirm Cancellation'
            : 'Mark as Paid'
        }
        icon='pi pi-check'
        disabled={loading}
        loading={loading}
        onClick={handleSave}
      />
    </div>
  )

  return (
    <Modals
      ModalHeader='Payment Transaction'
      visible={visible}
      btnName='Update'
      setVisible={setVisible}
      footer={footerContent}
      showFooter={true}
      showDefaultButton={false}
      showCloseButton={true}
      onButtonClick={handleSave}
    >
      <Toast ref={toast} />
      <div className='p-fluid'>
        <div className='field mb-4'>
          <label htmlFor='paymentStatus' className='font-bold mb-2'>
            Payment Status
          </label>
          <Dropdown
            id='paymentStatus'
            value={transactionData.paymentStatus}
            options={statusOptions}
            onChange={(e) => handleChange('paymentStatus', e.value)}
            optionLabel='name'
            optionValue='code'
            className='w-full'
          />
        </div>

        {isPaid && (
          <>
            <div className='field mb-4'>
              <label htmlFor='paymentDate' className='font-bold mb-2'>
                Payment Date
              </label>
              <Calendar
                id='paymentDate'
                value={transactionData.paymentDate}
                onChange={(e) => handleChange('paymentDate', e.value)}
                showIcon
                dateFormat='dd/mm/yy'
                className='w-full'
              />
            </div>

            <div className='field mb-4'>
              <label htmlFor='transactionId' className='font-bold mb-2'>
                Transaction ID
              </label>
              <InputText
                id='transactionId'
                value={transactionData.transactionId}
                onChange={(e) => handleChange('transactionId', e.target.value)}
                className='w-full'
              />
            </div>

            {!transactionData.transactionId && (
              <div
                className='p-2 mb-3 rounded-2'
                style={{
                  backgroundColor: '#fff7e6',
                  color: '#ff9800',
                  border: '1px solid #ff9800',
                }}
              >
                <i className='pi pi-exclamation-triangle mr-2'></i>
                Transaction ID is recommended for paid status
              </div>
            )}
          </>
        )}

        {isCancelled && (
          <>
            <div className='field mb-4'>
              <label htmlFor='paReason' className='font-bold mb-2'>
                Reason for Cancellation
              </label>
              <InputTextarea
                id='paReason'
                value={transactionData.paReason}
                onChange={(e) => handleChange('paReason', e.target.value)}
                rows={3}
                className='w-full'
              />
            </div>

            <div
              className='p-2 mb-3 rounded-2'
              style={{
                backgroundColor: '#fff1f0',
                color: '#EF4444',
                border: '1px solid #EF4444',
              }}
            >
              <i className='pi pi-info-circle mr-2'></i>
              Cancelled payments cannot be modified afterward
            </div>
          </>
        )}

        {isPending && (
          <div
            className='p-2 mb-3 rounded-2'
            style={{
              backgroundColor: '#f0f7ff',
              color: '#1976d2',
              border: '1px solid #1976d2',
            }}
          >
            <i className='pi pi-info-circle mr-2'></i>
            Set to "Paid" to enter payment details
          </div>
        )}
      </div>
    </Modals>
  )
}
